import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * "Install Nomad Life" prompt banner.
 *
 * Android Chrome fires a `beforeinstallprompt` event when the PWA passes
 * install criteria (manifest.json valid, service worker registered, over
 * HTTPS). We intercept, stash the deferred prompt, and only surface our own
 * branded banner. iOS Safari has no such event so we show a different hint
 * pointing to "Share → Add to Home Screen".
 *
 * The banner is dismissible with localStorage persistence so we don't nag
 * repeatedly.
 */
const DISMISS_KEY = "nomadlife_pwa_install_dismissed_at";
const REMIND_AFTER_DAYS = 14;
const FALLBACK_DELAY_MS = 6000;

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Chrome, Edge, most Chromium
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroidChrome() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // Chromium-based mobile browsers on Android — Chrome, Edge, Brave, Samsung
  return /Android/.test(ua) && /Chrome|Chromium|Edg|SamsungBrowser/.test(ua);
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    return (Date.now() - ts) < REMIND_AFTER_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [showAndroidFallback, setShowAndroidFallback] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (recentlyDismissed()) return undefined;

    // Android / desktop Chrome path — event fires when eligible. If it does,
    // cancel the fallback timer so we don't show two banners.
    let fallbackTimer = null;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      setShowAndroidFallback(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS path — no event; show a soft "how to install" hint 4s after page load.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShowIOSHint(true), 4000);
    } else if (isAndroidChrome()) {
      // Android Chrome fallback — Chrome sometimes never fires
      // beforeinstallprompt because of its opaque "engagement" heuristic. If
      // 6s pass without the event firing, show a manual instructions banner
      // so visitors always know they can install.
      fallbackTimer = setTimeout(() => setShowAndroidFallback(true), FALLBACK_DELAY_MS);
    }

    // When installation completes, hide the banner immediately.
    const onInstalled = () => {
      setVisible(false);
      setShowIOSHint(false);
      setShowAndroidFallback(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* user dismissed */ }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setShowIOSHint(false);
    setShowAndroidFallback(false);
  };

  // Chrome / Android install banner
  if (visible) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 p-4 flex items-start gap-3 fade-in"
        style={{
          background: "rgba(22, 39, 29, 0.98)",
          border: "1px solid var(--hh-accent)",
          backdropFilter: "blur(8px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        }}
        data-testid="pwa-install-banner"
      >
        <Download size={20} color="var(--hh-accent)" className="mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-display text-lg leading-tight">Install Nomad Life</p>
          <p className="text-xs text-[var(--hh-sage)] mt-1 leading-snug">
            Add the app to your home screen for one-tap access, offline reading, and a full-screen experience.
          </p>
          <div className="flex gap-2 mt-3">
            <button onClick={install} className="btn-primary text-xs" data-testid="pwa-install-accept">
              Install
            </button>
            <button onClick={dismiss} className="btn-ghost text-xs" data-testid="pwa-install-later">
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="text-[var(--hh-sage)] hover:text-[var(--hh-cream)] flex-shrink-0"
          data-testid="pwa-install-close"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // iOS Safari hint — no automatic install; guide the user through Share sheet
  if (showIOSHint) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:max-w-md md:left-1/2 md:-translate-x-1/2 z-50 p-4 fade-in"
        style={{
          background: "rgba(22, 39, 29, 0.98)",
          border: "1px solid var(--hh-border)",
          backdropFilter: "blur(8px)",
        }}
        data-testid="pwa-install-ios-hint"
      >
        <div className="flex items-start gap-3">
          <Download size={18} color="var(--hh-accent)" className="mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--hh-cream)]">
              Tap <span aria-label="share">⎋</span> in the Safari toolbar, then <strong>&quot;Add to Home Screen&quot;</strong> to install Nomad Life as an app.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="text-[var(--hh-sage)] hover:text-[var(--hh-cream)] flex-shrink-0"
            data-testid="pwa-install-ios-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Android Chrome fallback — appears after 6s if Chrome hasn't fired the
  // beforeinstallprompt event (which often happens because of its opaque
  // engagement heuristics). Points the user at the manual Chrome menu.
  if (showAndroidFallback) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:max-w-md md:left-1/2 md:-translate-x-1/2 z-50 p-4 fade-in"
        style={{
          background: "rgba(22, 39, 29, 0.98)",
          border: "1px solid var(--hh-border)",
          backdropFilter: "blur(8px)",
        }}
        data-testid="pwa-install-android-fallback"
      >
        <div className="flex items-start gap-3">
          <Download size={18} color="var(--hh-accent)" className="mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--hh-cream)] leading-relaxed">
              Install Nomad Life on your phone: tap the Chrome menu <strong>⋮</strong> (top-right), then <strong>&quot;Install app&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="text-[var(--hh-sage)] hover:text-[var(--hh-cream)] flex-shrink-0"
            data-testid="pwa-install-android-fallback-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
